import { useEffect, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useToast } from '@wartek-id/toast-v2'
import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Select } from '@wartek-id/select'

import { PredikatLabel } from 'app/KinerjaShared/components/PredikatLabel'
import {
  PRAKTIK_KINERJA_RATING_LABEL,
  PERILAKU_KERJA_RATING_LABEL,
} from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import { usePenetapanPredikatTableStore } from 'app/KinerjaShared/components/PenetapanPredikatTable/Provider'
import {
  Banner,
  BannerTitle,
  BannerDescription,
} from 'app/KinerjaShared/components/Banner'
import { generatePredikatKinerja } from 'app/KinerjaShared/utils/penetapan-predikat-kinerja'

import type { PenetapanPredikatKinerjaStoreItem } from 'app/KinerjaShared/stores/penetapan-predikat-kinerja-store'
import type {
  TPerformancePracticeResult,
  TWorkBehaviorResult,
} from 'types/kinerja-pelaksanaan'

type ChangeActionProps = {
  children?: React.ReactNode
  disabled?: boolean
  id?: string
  isError?: boolean
  isLoading?: boolean
  isSuccess?: boolean
  contents?: {
    successMessage?: string
    errorMessage?: string
    bannerTitle?: string
    bannerDescription?: string
  }
  onSubmit?: (
    items: Array<PenetapanPredikatKinerjaStoreItem>,
    data: ChangeActionFormFields
  ) => void
}

export type ChangeActionFormFields = {
  praktikKinerja: TPerformancePracticeResult
  perilakuKerja: TWorkBehaviorResult
}

// Default contents
const DEFAULT_SUCCESS_MESSAGE = 'Berhasil simpan penetapan.'
const DEFAULT_ERROR_MESSAGE = 'Gagal simpan penetapan. Coba lagi.'
const DEFAULT_BANNER_TITLE =
  'Rating akan disimpan untuk semua nama yang dipilih'
const DEFAULT_BANNER_DESCRIPTION =
  'Pastikan Anda sudah cek rekomendasi dan mempertimbangkan kinerja tiap nama.'

export function ChangeAction({
  children,
  disabled,
  id,
  isError,
  isLoading,
  isSuccess,
  contents = {
    successMessage: DEFAULT_SUCCESS_MESSAGE,
    errorMessage: DEFAULT_ERROR_MESSAGE,
    bannerTitle: DEFAULT_BANNER_TITLE,
    bannerDescription: DEFAULT_BANNER_DESCRIPTION,
  },
  onSubmit,
}: ChangeActionProps) {
  const [open, setOpen] = useState(false)
  const toast = useToast()
  const { selectedItems } = usePenetapanPredikatTableStore((store) => ({
    selectedItems: store.selectedItems,
  }))

  // -------------------------------------
  // Handle form fields
  // -------------------------------------
  const formMethods = useForm<ChangeActionFormFields>({
    mode: 'onChange',
  })

  // Reset the selected value upon closing the form sheet
  useEffect(() => {
    if (!open) {
      formMethods.reset({
        praktikKinerja: undefined,
        perilakuKerja: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const [praktikKinerja, perilakuKerja] = formMethods.watch([
    'praktikKinerja',
    'perilakuKerja',
  ])

  const predikatKinerja = useMemo(() => {
    return generatePredikatKinerja(praktikKinerja, perilakuKerja)
  }, [praktikKinerja, perilakuKerja])

  // -------------------------------------
  // Handle submission
  // -------------------------------------
  const handleSubmit = (data: ChangeActionFormFields) => {
    onSubmit?.(Array.from(selectedItems.values()), data)
  }

  // -------------------------------------
  // Handle close dialog when loading is done
  // -------------------------------------
  useEffect(() => {
    if (!isLoading) {
      setOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  useEffect(() => {
    if (isError) {
      toast({
        message: contents.errorMessage,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  useEffect(() => {
    if (isSuccess) {
      toast({
        message: contents.successMessage,
        type: 'success',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  return (
    <>
      <Button
        id={id}
        color="white"
        size="lg"
        disabled={selectedItems.size <= 0 || disabled}
        onClick={() => setOpen(true)}
      >
        {children}
      </Button>

      <Dialog
        onClose={() => setOpen(false)}
        size="md"
        isOpen={open}
        closeOnOverlayClick={false}
      >
        <DialogOverlay />
        <DialogContent aria-labelledby="bulk-penetapan-dialog-title">
          <section>
            <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
              <header className="p-6 bg-blue-80 rounded-t">
                <Text
                  variant="heading-lg"
                  color="inverse"
                  as="h2"
                  id="bulk-penetapan-dialog-title"
                >
                  Lakukan Sekaligus
                </Text>
              </header>

              <div className="p-5 space-y-6">
                <Banner variant="warning">
                  <BannerTitle>{contents.bannerTitle}</BannerTitle>
                  <BannerDescription>
                    {contents.bannerDescription}
                  </BannerDescription>
                </Banner>
                <div className="bg-surface-subdued rounded-lg p-6 space-y-4">
                  <Text variant="heading-sm">Nama yang Dipilih:</Text>
                  <ul className="grid grid-cols-2 gap-3 overflow-auto max-h-40">
                    {Array.from(selectedItems.values()).map((item) => (
                      <li key={item.id} className="flex items-center space-x-3">
                        <span className="w-3 h-3 rounded-full bg-blue-80 inline-block"></span>
                        <Text className="overflow-wrap-anywhere">
                          {item.name}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <fieldset>
                    <Text
                      as="label"
                      htmlFor="rating-praktik-kinerja"
                      variant="heading-sm"
                      className="mb-2 block"
                    >
                      Rating Praktik Kinerja
                    </Text>

                    <Controller
                      control={formMethods.control}
                      rules={{ required: true }}
                      name="praktikKinerja"
                      render={({ field: { onChange, value } }) => {
                        return (
                          <Select
                            id="rating-praktik-kinerja"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                          >
                            <option value="">Pilih rating</option>

                            {Object.entries(PRAKTIK_KINERJA_RATING_LABEL).map(
                              ([key, label]) => (
                                <option key={key} value={key}>
                                  {label}
                                </option>
                              )
                            )}
                          </Select>
                        )
                      }}
                    />
                  </fieldset>

                  <fieldset>
                    <Text
                      as="label"
                      htmlFor="rating-perilaku-kerja"
                      variant="heading-sm"
                      className="mb-2 block"
                    >
                      Rating Perilaku Kerja
                    </Text>

                    <Controller
                      control={formMethods.control}
                      rules={{ required: true }}
                      name="perilakuKerja"
                      render={({ field: { onChange, value } }) => {
                        return (
                          <Select
                            id="rating-perilaku-kerja"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                          >
                            <option value="">Pilih rating</option>
                            {Object.entries(PERILAKU_KERJA_RATING_LABEL).map(
                              ([key, label]) => (
                                <option key={key} value={key}>
                                  {label}
                                </option>
                              )
                            )}
                          </Select>
                        )
                      }}
                    />
                  </fieldset>
                  <fieldset>
                    <Text
                      as="label"
                      htmlFor="predikat-kinerja"
                      variant="heading-sm"
                      className="mb-2 block"
                    >
                      Predikat Kinerja
                    </Text>

                    <PredikatLabel
                      value={predikatKinerja}
                      defaultLabel="Predikat akan muncul secara otomatis setelah semua Rating dipilih"
                    />
                  </fieldset>
                </div>
              </div>

              <footer className="p-6 flex items-center justify-between gap-6 border border-t border-disabled">
                <Text variant="body-lg" className="!font-bold">
                  {selectedItems.size} Nama Dipilih
                </Text>

                <div className="flex gap-3">
                  <Button
                    size="lg"
                    color="white"
                    disabled={isLoading}
                    onClick={() => setOpen(false)}
                  >
                    Kembali
                  </Button>
                  <Button
                    size="lg"
                    color="blue"
                    type="submit"
                    disabled={!formMethods.formState.isValid || isLoading}
                    isLoading={isLoading}
                  >
                    Simpan
                  </Button>
                </div>
              </footer>
            </form>
          </section>
        </DialogContent>
      </Dialog>
    </>
  )
}
